import React, { useEffect, useState } from "react";
import { EventsType, useIdleTimer, workerTimers } from "react-idle-timer";
import IdleTimerModals from "./IdleTimerModals";
import useModal from "hooks/useModal";
import useSessionExpiration from "hooks/useSessionExpiration";
import { setTimeoutMin2Ms } from "utils/cookieUtils";
import {
  formatDuration,
  sessionDebugLog,
} from "utils/sessionExpirationUtils";

export interface IdleTimerContainerProps {
  isImpersonation?: boolean;
}

const MILLISECONDS_PER_SECOND = 1000;
const IDLE_WARNING_MS = 300000; // 5 minutes of warning before logout.
// Inactivity before the warning. Fallback: 10 minutes.
const IDLE_TIMEOUT_MS = import.meta.env.VITE_REACT_APP_TIMEOUT_MIN
  ? setTimeoutMin2Ms(import.meta.env.VITE_REACT_APP_TIMEOUT_MIN)
  : 600000;
const TOTAL_IDLE_MS = IDLE_TIMEOUT_MS + IDLE_WARNING_MS;
// Default events minus visibilitychange/focus: switching tabs is not user activity.
const ACTIVITY_EVENTS: EventsType[] = [
  "mousemove",
  "keydown",
  "wheel",
  "mousedown",
  "touchstart",
  "touchmove",
];

const toSeconds = (ms: number) => Math.ceil(ms / MILLISECONDS_PER_SECOND);

/** Coordina la inactividad del usuario y delega la expiración del token al hook de sesión. */
const IdleTimerContainer: React.FC<IdleTimerContainerProps> = ({
  isImpersonation = false,
}) => {
  const { collapsed, handleOpenModal, handleCloseModal } = useModal();
  const [idleSecondsRemaining, setIdleSecondsRemaining] = useState(
    toSeconds(IDLE_WARNING_MS),
  );
  const isIdlePromptOpen = !collapsed && !isImpersonation;

  const {
    isTokenExpired,
    isTokenWarningVisible,
    isRefreshingToken,
    tokenSecondsRemaining,
    endSession,
    refreshToken,
  } = useSessionExpiration({
    isImpersonation,
    onImpersonationExpired: handleOpenModal,
  });

  const { activate, getRemainingTime, isIdle } = useIdleTimer({
    timeout: TOTAL_IDLE_MS,
    promptBeforeIdle: IDLE_WARNING_MS,
    events: ACTIVITY_EVENTS,
    onPrompt: () => {
      sessionDebugLog("idle prompt shown", {
        remaining: formatDuration(getRemainingTime()),
      });
      setIdleSecondsRemaining(toSeconds(getRemainingTime()));
      handleOpenModal();
    },
    onIdle: () => {
      sessionDebugLog("idle timeout reached, ending session");
      endSession();
    },
    // Also fires when another tab calls activate() via crossTab.
    onActive: () => {
      sessionDebugLog("user active again");
      handleCloseModal();
    },
    disabled: isImpersonation || isTokenWarningVisible,
    timers: workerTimers,
    crossTab: true,
    syncTimers: 200,
  });

  const continueIdleSession = () => {
    sessionDebugLog("idle session continued by user");
    activate();
    handleCloseModal();
  };

  useEffect(() => {
    sessionDebugLog("idle timer mounted", {
      idleTimeout: formatDuration(IDLE_TIMEOUT_MS),
      warning: formatDuration(IDLE_WARNING_MS),
      isImpersonation,
    });
    return () => sessionDebugLog("idle timer unmounted");
  }, []);

  useEffect(() => {
    if (!isIdlePromptOpen) return;

    const id = workerTimers.setInterval(
      () => setIdleSecondsRemaining(toSeconds(getRemainingTime())),
      MILLISECONDS_PER_SECOND,
    );
    return () => workerTimers.clearInterval(id);
  }, [isIdlePromptOpen]);

  // Covers tabs frozen by the browser: worker timers resume late and would restart the prompt.
  useEffect(() => {
    if (isImpersonation || isTokenWarningVisible) return;

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || isIdle()) return;

      const remainingMs = getRemainingTime();
      sessionDebugLog("tab visible", { remaining: formatDuration(remainingMs) });
      if (remainingMs <= 0) endSession();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isImpersonation, isTokenWarningVisible]);

  return (
    <IdleTimerModals
      idleSecondsRemaining={idleSecondsRemaining}
      isImpersonation={isImpersonation}
      isRefreshingToken={isRefreshingToken}
      isTokenExpired={isTokenExpired}
      isTokenWarningVisible={isTokenWarningVisible}
      modalCollapsed={collapsed}
      tokenSecondsRemaining={tokenSecondsRemaining}
      onContinueIdleSession={continueIdleSession}
      onRefreshToken={refreshToken}
    />
  );
};

export default IdleTimerContainer;